import { ChevronDown } from 'lucide-react';
import { useId } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Role, StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { FormControl, FormField, FormItem, FormLabel } from '@nucleus-commerce/ui/components/form';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { ACCESS_SCOPE, type EditMemberInput } from '../schemas/member-form-schema';
import { isOwner } from '../schemas/member-rules';
import { permissionGroups } from '../schemas/role-rules';

import { PermissionChecklist } from './permission-checklist';

/** Radix Select refuses an empty value; this one stands for "the store uses the default role". */
const DEFAULT_ROLE = '__default';

const EVERYTHING = ['*'];

const ALL_GRANTABLE: readonly string[] = permissionGroups().flatMap((group) => group.permissions);

type StoreRoleRow = EditMemberInput['storeRoles'][number];

interface OverridePairProps {
  idPrefix: string;
  rolePermissions: readonly string[];
  granted: readonly string[];
  revoked: readonly string[];
  held: readonly string[];
  onGranted: (value: string[]) => void;
  onRevoked: (value: string[]) => void;
}

/** "Extra" lists what the role lacks; "Removed" lists what the role holds. */
function OverridePair({
  idPrefix,
  rolePermissions,
  granted,
  revoked,
  held,
  onGranted,
  onRevoked,
}: OverridePairProps) {
  const { t } = useTranslation('team');
  const notInRole = ALL_GRANTABLE.filter((permission) => !rolePermissions.includes(permission));
  const inRole = ALL_GRANTABLE.filter((permission) => rolePermissions.includes(permission));
  return (
    <div className="grid gap-2">
      <Collapsible defaultOpen={granted.length > 0}>
        <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm font-medium">
          <span>{t('overrides.extra', { count: granted.length })}</span>
          <ChevronDown
            aria-hidden
            className="size-4 transition-transform group-data-[state=open]:rotate-180"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          <p className="text-muted-foreground mb-3 text-xs">{t('overrides.extraHint')}</p>
          <PermissionChecklist
            idPrefix={`${idPrefix}-granted`}
            value={granted}
            onChange={onGranted}
            held={held}
            only={notInRole}
          />
        </CollapsibleContent>
      </Collapsible>
      <Collapsible defaultOpen={revoked.length > 0}>
        <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm font-medium">
          <span>{t('overrides.removed', { count: revoked.length })}</span>
          <ChevronDown
            aria-hidden
            className="size-4 transition-transform group-data-[state=open]:rotate-180"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          <p className="text-muted-foreground mb-3 text-xs">{t('overrides.removedHint')}</p>
          {inRole.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('overrides.nothingToRemove')}</p>
          ) : (
            <PermissionChecklist
              idPrefix={`${idPrefix}-revoked`}
              value={revoked}
              onChange={onRevoked}
              held={EVERYTHING}
              only={inRole}
            />
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export interface MemberPermissionOverridesProps {
  /** Every role, to read what each holds. */
  roles: readonly Role[];
  /** Roles the signed-in member may hand out (see `assignableRoles`). */
  assignable: readonly Role[];
  /** Stores the signed-in member can see, from /me. */
  stores: readonly StaffProfileStoresItem[];
  /** What the signed-in member holds; nobody grants beyond it. */
  held: readonly string[];
}

/**
 * Extra and removed permissions on top of the default role, and per store a different role with
 * its own extras and removals. The owner holds everything everywhere, so it shows a note instead.
 */
export function MemberPermissionOverrides({
  roles,
  assignable,
  stores,
  held,
}: MemberPermissionOverridesProps) {
  const { t } = useTranslation('team');
  const form = useFormContext<EditMemberInput>();
  const idPrefix = useId();
  const roleKey = form.watch('roleKey');
  const access = form.watch('access');
  const storeIds = form.watch('storeIds');
  const scope = form.watch('scope');
  const granted = form.watch('grantedPermissions');
  const revoked = form.watch('revokedPermissions');
  const storeRoles = form.watch('storeRoles');

  if (isOwner(roleKey)) {
    return <p className="text-muted-foreground text-sm">{t('overrides.ownerFixed')}</p>;
  }

  const permissionsOf = (key: string): readonly string[] =>
    roles.find((role) => role.key === key)?.permissions ?? [];
  const reachable = access === 'all' ? stores : stores.filter((s) => storeIds.includes(s.id));
  const defaultRoleName = roles.find((role) => role.key === roleKey)?.name ?? roleKey;
  const setPermissions = (name: 'grantedPermissions' | 'revokedPermissions', value: string[]) =>
    form.setValue(name, value, { shouldDirty: true });
  const setStoreRoles = (value: StoreRoleRow[]) =>
    form.setValue('storeRoles', value, { shouldDirty: true });

  const rowOf = (storeId: string): StoreRoleRow =>
    storeRoles.find((row) => row.storeId === storeId) ?? {
      storeId,
      roleKey: '',
      grantedPermissions: [],
      revokedPermissions: [],
    };
  const updateRow = (storeId: string, patch: Partial<StoreRoleRow>) => {
    const next = { ...rowOf(storeId), ...patch };
    setStoreRoles([...storeRoles.filter((row) => row.storeId !== storeId), next]);
  };

  return (
    <div className="flex flex-col gap-4" data-tour="org.team.permissions">
      <section className="grid gap-2">
        <h3 className="text-sm font-medium">{t('overrides.title')}</h3>
        <p className="text-muted-foreground text-xs">
          {t('overrides.description', { role: defaultRoleName })}
        </p>
        <OverridePair
          idPrefix={`${idPrefix}-default`}
          rolePermissions={permissionsOf(roleKey)}
          granted={granted}
          revoked={revoked}
          held={held}
          onGranted={(value) => setPermissions('grantedPermissions', value)}
          onRevoked={(value) => setPermissions('revokedPermissions', value)}
        />
      </section>

      <FormField
        control={form.control}
        name="scope"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('overrides.scope')}</FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="grid gap-2 sm:grid-cols-2"
              >
                {ACCESS_SCOPE.map((option) => (
                  <label
                    key={option}
                    htmlFor={`${idPrefix}-scope-${option}`}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                      field.value === option && 'border-primary bg-primary-tint/40',
                    )}
                  >
                    <RadioGroupItem
                      id={`${idPrefix}-scope-${option}`}
                      value={option}
                      className="mt-0.5"
                    />
                    <span className="grid gap-0.5 text-sm">
                      <span className="font-medium">{t(`overrides.scopes.${option}`)}</span>
                      <span className="text-muted-foreground text-xs">
                        {t(`overrides.scopes.${option}Hint`)}
                      </span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />

      {scope === 'perStore' ? (
        reachable.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('fields.noStores')}</p>
        ) : (
          <ul className="flex flex-col gap-3" aria-label={t('overrides.storesLabel')}>
            {reachable.map((store) => {
              const row = rowOf(store.id);
              const own = row.roleKey !== '';
              return (
                <li key={store.id} className="rounded-md border p-3" data-testid="store-role-row">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{store.name}</span>
                      <span className="text-muted-foreground block truncate text-xs">
                        {store.slug}
                      </span>
                    </span>
                    <Select
                      value={own ? row.roleKey : DEFAULT_ROLE}
                      onValueChange={(value) =>
                        updateRow(store.id, {
                          roleKey: value === DEFAULT_ROLE ? '' : value,
                          grantedPermissions: [],
                          revokedPermissions: [],
                        })
                      }
                    >
                      <SelectTrigger
                        className="w-56"
                        aria-label={t('overrides.storeRole', { store: store.name })}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DEFAULT_ROLE}>
                          {t('overrides.useDefault', { role: defaultRoleName })}
                        </SelectItem>
                        {assignable
                          .filter((role) => !isOwner(role.key))
                          .map((role) => (
                            <SelectItem key={role.key} value={role.key}>
                              {role.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {own ? (
                    <div className="mt-3">
                      <OverridePair
                        idPrefix={`${idPrefix}-${store.id}`}
                        rolePermissions={permissionsOf(row.roleKey)}
                        granted={row.grantedPermissions}
                        revoked={row.revokedPermissions}
                        held={held}
                        onGranted={(value) => updateRow(store.id, { grantedPermissions: value })}
                        onRevoked={(value) => updateRow(store.id, { revokedPermissions: value })}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </div>
  );
}
